import { useState } from "react";

import { SEOHead } from "@/components/helpers/seo/SEOHead";
import { Box, Flex } from "@/components/layout/flexbox";
import { Media } from "@/components/layout/Media";
import { Tabs } from "@/components/pieces/Tabs";
import { LIGHTEST_BACKGROUND, SITE_NAME } from "@/css/constants";
import { FullMeeting, FullReview, UpcomingMeeting } from "@/types/meetings";
import { Tab } from "@/types/tabs";

import { MeetingBasics } from "./meetings/MeetingBasics";
import { MeetingSidebar } from "./meetings/MeetingSidebar";
import { PortalContainer } from "./PortalContainer";
import { ReviewBasics } from "./reviews/ReviewBasics";
import { ReviewSidebar } from "./reviews/review-sidebar";
import { ServiceMessages } from "./ServiceMessages";

const TABS: Tab[] = [
  { key: "overview", label: "Overview" },
  { key: "chat", label: "Messages" },
];

interface ServiceDetailsProps {
  meeting?: UpcomingMeeting | FullMeeting;
  review?: FullReview;
  customerId?: string;
  onClose?: () => void;
}

export function ServiceDetails({
  meeting,
  review,
  customerId,
  onClose,
}: ServiceDetailsProps) {
  const [activeTab, setActiveTab] = useState<Tab["key"]>("overview");
  const tab = TABS.find((t) => t.key === activeTab);
  const object = meeting || review;
  const objectPath = meeting ? "session" : "review";

  const backUrl = customerId
    ? `/client/${customerId}/${objectPath}s`
    : `/${objectPath}s`;

  const chatChannelId = meeting?.chatChannelId;

  const headerContent = meeting ? (
    <MeetingBasics
      meeting={meeting}
      chatChannelId={chatChannelId}
      large={true}
      showMessageButton={false}
      customerId={customerId}
    />
  ) : (
    review && (
      <ReviewBasics
        review={review}
        chatChannelId={review.chatChannelId}
        large={true}
        showMessageButton={false}
        customerId={customerId}
      />
    )
  );

  const sidebarContent = meeting ? (
    <MeetingSidebar meeting={meeting} />
  ) : (
    review && <ReviewSidebar review={review} />
  );

  const messages =
    object && object.chatChannelId ? (
      <ServiceMessages
        object={{
          chatChannelId: object.chatChannelId,
          customerId: object.customerId!,
        }}
      />
    ) : null;

  return (
    <>
      <PortalContainer
        active={true}
        onCancel={onClose}
        backUrl={backUrl}
        closeOnFocusEsc={true}
      >
        <SEOHead title={`${object?.product?.name} - ${SITE_NAME}`} />
        <Box p={[3, 3, 4]}>{headerContent}</Box>
        <Media at="xs">
          {sidebarContent && tab ? (
            <>
              <Tabs
                tabs={TABS}
                tab={tab}
                onTabChange={(newTab) => setActiveTab(newTab.key)}
              />
              {tab?.key === "overview" ? (
                <Box pt={1} backgroundColor={LIGHTEST_BACKGROUND}>
                  {sidebarContent}
                </Box>
              ) : (
                messages
              )}
            </>
          ) : (
            messages
          )}
        </Media>
        <Media greaterThan="xs">
          <Flex>
            {sidebarContent ? (
              <Box
                width="40%"
                backgroundColor="#FFF"
                style={{
                  borderTop: "1px solid #e2e2e2",
                  borderRight: "1px solid #e2e2e2",
                }}
              >
                {sidebarContent}
              </Box>
            ) : null}
            <Box width={sidebarContent ? "60%" : 1}>{messages}</Box>
          </Flex>
        </Media>
      </PortalContainer>
    </>
  );
}
